import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { FileText } from 'lucide-react';
import { Concession } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { STATUS_COLORS, STATUS_TIMELINE } from './constants';

interface SummaryCardProps {
  concession: Concession;
}

export function SummaryCard({ concession }: SummaryCardProps) {
  const { formatDateTime } = useFormatters();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-muted-foreground">Customer</Label>
            <p className="font-medium">{concession.customer_name}</p>
          </div>
          <div>
            <Label className="text-muted-foreground">Supplier</Label>
            <p className="font-medium">{concession.supplier_name}</p>
          </div>
          <div>
            <Label className="text-muted-foreground">Requested By</Label>
            <p className="font-medium">{concession.requested_by_name}</p>
          </div>
          <div>
            <Label className="text-muted-foreground">Requested Date</Label>
            <p className="font-medium">{formatDateTime(concession.requested_date)}</p>
          </div>
          {concession.po_number && (
            <div>
              <Label className="text-muted-foreground">PO Number</Label>
              <p className="font-medium">{concession.po_number}</p>
            </div>
          )}
          {/* CR-6510: the legacy summary named the line as well as the order. */}
          {concession.po_line_details?.line_number != null && (
            <div>
              <Label className="text-muted-foreground">PO Line</Label>
              <p className="font-medium">{concession.po_line_details.line_number}</p>
            </div>
          )}
          {concession.grn_number && (
            <div>
              <Label className="text-muted-foreground">GRN Number</Label>
              <p className="font-medium">{concession.grn_number}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {STATUS_TIMELINE.map((status) => {
              // Handle rejected status specially
              const displayStatus = status === 'approved' && concession.status === 'rejected' ? 'rejected' : status;
              const config = STATUS_COLORS[displayStatus];
              const Icon = config?.icon || FileText;
              const isActive = concession.status === displayStatus;
              const statusOrder = ['draft', 'submitted', 'under_review', 'approved', 'rejected', 'closed'];
              const isPast = statusOrder.indexOf(concession.status) > statusOrder.indexOf(displayStatus);

              return (
                <div key={status} className={`flex items-center gap-3 ${isActive ? 'text-primary' : isPast ? 'text-muted-foreground' : 'text-muted-foreground/50'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isActive ? 'bg-primary text-primary-foreground' : isPast ? 'bg-muted' : 'bg-muted/50'}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-medium">{config?.label || status}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
